import { useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Heading2, Bold, List, ListOrdered } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Renders `Tag` with a fixed class; drops react-markdown's `node` prop so it doesn't reach the DOM.
const styled = (Tag, className, extra = {}) => ({ node, children, ...props }) => (
  <Tag className={className} {...extra} {...props}>{children}</Tag>
);

// Tailwind preflight strips heading/list styles, so the preview restores them per element.
const PREVIEW_COMPONENTS = {
  h1: styled('h1', 'text-lg font-bold mt-3 mb-1 first:mt-0'),
  h2: styled('h2', 'text-base font-bold mt-3 mb-1 first:mt-0'),
  h3: styled('h3', 'text-sm font-bold mt-2 mb-1 first:mt-0'),
  p: styled('p', 'my-1.5'),
  ul: styled('ul', 'list-disc pl-5 my-1.5 space-y-0.5'),
  ol: styled('ol', 'list-decimal pl-5 my-1.5 space-y-0.5'),
  strong: styled('strong', 'font-semibold'),
  a: styled('a', 'text-blue-600 underline', { target: '_blank', rel: 'noreferrer' }),
};

// Line-prefix formats: `prefix(i)` builds the marker for the i-th selected line, `match` detects an existing one.
const LINE_FORMATS = {
  heading: { prefix: () => '## ', match: /^#{1,6} / },
  bullet: { prefix: () => '- ', match: /^[-*] / },
  numbered: { prefix: i => `${i + 1}. `, match: /^\d+\. / },
};

/** Markdown textarea with a small formatting toolbar and a Write/Preview toggle. Value is plain Markdown. */
export default function MarkdownEditor({ value, onChange, placeholder, maxLength, rows = 8 }) {
  const { t } = useTranslation();
  const ref = useRef(null);
  const [preview, setPreview] = useState(false);

  // Applies the edit, then restores focus and the selection once React has re-rendered the textarea.
  const commit = (next, selStart, selEnd) => {
    onChange(next);
    requestAnimationFrame(() => {
      const el = ref.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(selStart, selEnd);
    });
  };

  // Toggles a line marker on every line touched by the selection: removed if all lines have it, added otherwise.
  const formatLines = (kind) => {
    const { prefix, match } = LINE_FORMATS[kind];
    const el = ref.current;
    const start = value.lastIndexOf('\n', el.selectionStart - 1) + 1;
    const endIdx = value.indexOf('\n', el.selectionEnd);
    const end = endIdx === -1 ? value.length : endIdx;
    const lines = value.slice(start, end).split('\n');
    const allHave = lines.every(l => match.test(l));
    const block = lines
      .map((l, i) => {
        const bare = l.replace(/^#{1,6} |^[-*] |^\d+\. /, '');
        return allHave ? bare : prefix(i) + bare;
      })
      .join('\n');
    commit(value.slice(0, start) + block + value.slice(end), start, start + block.length);
  };

  const formatBold = () => {
    const el = ref.current;
    const { selectionStart: s, selectionEnd: e } = el;
    const next = value.slice(0, s) + '**' + value.slice(s, e) + '**' + value.slice(e);
    commit(next, s + 2, e + 2);
  };

  const tools = [
    { title: t('products.mdHeading'), Icon: Heading2, run: () => formatLines('heading') },
    { title: t('products.mdBold'), Icon: Bold, run: formatBold },
    { title: t('products.mdBullet'), Icon: List, run: () => formatLines('bullet') },
    { title: t('products.mdNumbered'), Icon: ListOrdered, run: () => formatLines('numbered') },
  ];

  const tabClass = active =>
    `px-2.5 py-1 text-xs rounded-md ${active ? 'bg-white shadow-sm text-gray-900 font-medium' : 'text-gray-500 hover:text-gray-700'}`;

  return (
    <div className="border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent">
      <div className="flex items-center justify-between gap-2 px-2 py-1.5 border-b border-gray-200 bg-gray-50 rounded-t-lg">
        <div className="flex items-center gap-0.5">
          {tools.map(({ title, Icon, run }) => (
            <button key={title} type="button" title={title} onClick={run} disabled={preview}
              className="p-1.5 rounded-md text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:hover:bg-transparent">
              <Icon size={15} />
            </button>
          ))}
        </div>
        <div className="flex items-center gap-0.5 bg-gray-100 rounded-lg p-0.5">
          <button type="button" className={tabClass(!preview)} onClick={() => setPreview(false)}>{t('products.write')}</button>
          <button type="button" className={tabClass(preview)} onClick={() => setPreview(true)}>{t('products.preview')}</button>
        </div>
      </div>

      {preview ? (
        <div className="px-3 py-2 text-sm text-gray-800 overflow-y-auto" style={{ minHeight: `${rows * 1.5}rem` }}>
          {value.trim()
            ? <ReactMarkdown components={PREVIEW_COMPONENTS}>{value}</ReactMarkdown>
            : <p className="text-gray-400">{placeholder}</p>}
        </div>
      ) : (
        <textarea ref={ref} value={value} onChange={e => onChange(e.target.value)} rows={rows} maxLength={maxLength}
          className="w-full px-3 py-2 text-sm rounded-b-lg focus:outline-none resize-y block"
          placeholder={placeholder} />
      )}

      <p className="px-3 py-1 text-[11px] text-gray-400 border-t border-gray-100">{t('products.formattedDescHint')}</p>
    </div>
  );
}
